import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity, BadgeCheck, Cable, ClipboardList, CreditCard, Gauge, LineChart, LogOut,
  Menu, Package, ReceiptText, RotateCcw, ShoppingCart, Users, Workflow, X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { NuatMark, NuatWordmark } from '@/components/ui';
import { LanguageToggle } from '@/components/language-toggle';
import { useLocale } from '@/i18n/locale-provider';
import type { Dictionary } from '@/i18n/dictionaries';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  /** Hidden when the signed-in user lacks this permission. */
  permission?: string;
}

function groupsFor(t: Dictionary): Array<{ heading: string; items: NavItem[] }> {
  return [
    {
      heading: t.nav.overview,
      items: [
        { to: '/', label: t.nav.dashboard, icon: Gauge },
        { to: '/analytics', label: t.nav.analytics, icon: LineChart, permission: 'analytics:read' },
      ],
    },
    {
      heading: t.nav.simulator,
      items: [
        { to: '/products', label: t.nav.products, icon: Package, permission: 'products:read' },
        { to: '/customers', label: t.nav.customers, icon: Users, permission: 'customers:read' },
        { to: '/orders', label: t.nav.orders, icon: ShoppingCart, permission: 'orders:read' },
        { to: '/invoices', label: t.nav.invoices, icon: ReceiptText, permission: 'invoices:read' },
        { to: '/payments', label: t.nav.payments, icon: CreditCard, permission: 'payments:read' },
        { to: '/returns', label: t.nav.returns, icon: RotateCcw, permission: 'returns:read' },
      ],
    },
    {
      heading: t.nav.automation,
      items: [
        { to: '/agent', label: t.nav.agent, icon: Workflow, permission: 'agent:read' },
        { to: '/connectors', label: t.nav.connectors, icon: Cable, permission: 'connectors:read' },
      ],
    },
    {
      heading: t.nav.governance,
      items: [
        { to: '/users', label: t.nav.users, icon: BadgeCheck, permission: 'users:read' },
        { to: '/audit', label: t.nav.audit, icon: ClipboardList, permission: 'audit:read' },
        { to: '/system', label: t.nav.system, icon: Activity },
      ],
    },
  ];
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { user, signOut, can } = useAuth();
  const { t, isRtl } = useLocale();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const groups = groupsFor(t);

  return (
    <div className="flex min-h-screen">
      {open && (
        <button
          type="button"
          aria-label={t.nav.close}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-deep-900/40 lg:hidden"
        />
      )}

      {/* The drawer slides in from the inline start, so it comes from the right in Arabic. */}
      <aside
        className={`fixed inset-y-0 start-0 z-40 flex w-64 flex-col border-e border-deep-800 bg-deep-900
                    transition-transform lg:static lg:translate-x-0
                    ${open ? 'translate-x-0' : isRtl ? 'translate-x-full' : '-translate-x-full'}`}
      >
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-deep-800 px-5">
          <NuatMark size={28} tone="inverse" />
          <span className="flex flex-col leading-none">
            <NuatWordmark height={12} className="text-white" />
            <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-glow-400">
              {t.nav.dashboard}
            </span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="ms-auto rounded p-1 text-mist-400 hover:text-white lg:hidden"
            aria-label={t.nav.close}
          >
            <X size={18} />
          </button>
        </div>

        <nav aria-label={t.nav.sections} className="flex-1 overflow-y-auto px-3 py-4">
          {groups.map((group) => {
            // Hide a whole group when the role cannot see anything in it.
            const visible = group.items.filter((item) => !item.permission || can(item.permission));
            if (!visible.length) return null;
            return (
              <div key={group.heading} className="mb-5">
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-mist-500">
                  {group.heading}
                </p>
                <ul className="space-y-0.5">
                  {visible.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-deep-700 font-medium text-white'
                              : 'text-mist-400 hover:bg-deep-800 hover:text-white'
                          }`
                        }
                      >
                        <item.icon size={16} aria-hidden />
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-deep-800 p-3">
          <div className="rounded-md bg-deep-800 px-3 py-2.5">
            <p className="truncate text-sm font-medium text-white">{user?.fullName}</p>
            <p className="ltr-embed truncate text-xs text-mist-500">{user?.email}</p>
            <p className="mt-1.5 truncate text-[10px] uppercase tracking-wide text-glow-400">
              {user?.roles.join(' · ')}
            </p>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <LanguageToggle tone="dark" />
            <button
              type="button"
              onClick={signOut}
              className="ms-auto flex items-center gap-2 rounded-md px-3 py-2 text-sm text-mist-400 hover:bg-deep-800 hover:text-white"
            >
              <LogOut size={15} aria-hidden />
              {t.app.signOut}
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-mist-200 bg-white px-5 lg:hidden">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-md p-2 text-mist-600 hover:bg-mist-100"
            aria-label={t.nav.open}
          >
            <Menu size={20} />
          </button>
          <NuatMark size={26} />
          <span className="font-display text-sm font-semibold text-deep-700">{t.app.name}</span>
          <span className="ms-auto"><LanguageToggle /></span>
        </header>

        {/* Keyed on pathname so each route starts scrolled to the top. */}
        <main key={location.pathname} className="min-w-0 flex-1 p-5 sm:p-7">
          {children}
        </main>
      </div>
    </div>
  );
}
